/**
 * EmailDetailModal - Shared email detail modal used by EmailList and InboxTile
 */

import React, { useState, Fragment } from 'react';
import { XMarkIcon, ArrowUturnLeftIcon, NoSymbolIcon, CheckCircleIcon, NewspaperIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { format, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Button, Spinner } from './ui';
import { apiClient } from '../api/apiConfig';

interface EmailData {
  id: number;
  subject: string;
  sender: string;
  sender_name?: string | null;
  recipients: string;
  cc_recipients?: string;
  body: string;
  html_body?: string;
  received_at: string;
  is_handled?: boolean;
  is_newsletter?: boolean;
  spam_score?: number | null;
  spam_flag?: boolean;
  spf_result?: string;
  dkim_result?: string;
  account_detail?: { id: number; email_address: string; display_name: string; provider: string; domain: string } | null;
}

interface EmailDetailModalProps {
  email: EmailData | null;
  loading?: boolean;
  onClose: () => void;
  onMarkHandled?: (emailId: number) => void;
  onBlockSender?: (emailId: number, blockType: 'email' | 'domain') => void;
  onMarkNewsletter?: (emailId: number) => void;
  onReply?: (email: EmailData) => void;
  onReplyAll?: (email: EmailData) => void;
}

/**
 * Detect if email body is plain text (no HTML tags) and convert to readable HTML.
 */
function formatEmailBody(body: string): string {
  if (/<\s*(div|p|table|br|blockquote|html|head|body|ul|ol|li|h[1-6])\b/i.test(body)) {
    return body;
  }

  const lines = body.split('\n');
  const result: string[] = [];
  let inQuote = false;

  for (const line of lines) {
    const quoteMatch = line.match(/^(>[\s>]*)/);
    const isQuotedLine = !!quoteMatch;

    if (isQuotedLine && !inQuote) {
      result.push('<blockquote style="margin:8px 0;padding:4px 0 4px 12px;border-left:3px solid #E5E7EB;color:#6B7280;">');
      inQuote = true;
    } else if (!isQuotedLine && inQuote) {
      result.push('</blockquote>');
      inQuote = false;
    }

    let content = isQuotedLine ? line.replace(/^>[\s>]*/, '') : line;

    content = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    content = content.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    if (/^-+\s*Forwarded message\s*-+$/.test(line.trim())) {
      content = `<hr style="border:none;border-top:1px solid #E5E7EB;margin:12px 0;"><strong style="color:#9CA3AF;font-size:12px;">${content}</strong>`;
    }

    if (content.trim() === '') {
      result.push('<br>');
    } else {
      result.push(`<div>${content}</div>`);
    }
  }

  if (inQuote) {
    result.push('</blockquote>');
  }

  return result.join('');
}

const EmailDetailModal: React.FC<EmailDetailModalProps> = ({
  email,
  loading = false,
  onClose,
  onMarkHandled,
  onBlockSender,
  onMarkNewsletter,
  onReply,
  onReplyAll,
}) => {
  const [closeButtonHovered, setCloseButtonHovered] = useState(false);

  const isOpen = email !== null || loading;

  const bodyHtml = email?.html_body || email?.body || '';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)' }} />
        </Transition.Child>

        {/* Modal Panel Container */}
        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: window.innerWidth < 768 ? 'stretch' : 'center',
            justifyContent: 'center',
            padding: window.innerWidth < 768 ? 0 : SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: window.innerWidth < 768 ? '100%' : '900px',
                minHeight: window.innerWidth < 768 ? '100vh' : 'auto',
                maxHeight: window.innerWidth < 768 ? '100vh' : '90vh',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: window.innerWidth < 768 ? 0 : '16px',
                backgroundColor: COLORS.bg.panel,
                padding: window.innerWidth < 768 ? SPACING[16] : SPACING[32],
                textAlign: 'left',
                verticalAlign: 'middle',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {loading && !email ? (
                  <div style={{ padding: SPACING[48], textAlign: 'center' }}><Spinner size="md" /></div>
                ) : email && (
                  <>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: `1px solid ${COLORS.border.default}`, paddingBottom: SPACING[16], marginBottom: SPACING[16] }}>
                      <div>
                        <Dialog.Title as="h3" style={{ fontSize: TYPOGRAPHY.size['2xl'], fontWeight: TYPOGRAPHY.weight.semibold, lineHeight: '1.4', color: COLORS.text.primary }}>
                          {email.subject}
                        </Dialog.Title>
                        <p style={{ marginTop: SPACING[8], fontSize: TYPOGRAPHY.size.md, color: COLORS.text.secondary }}>
                          <strong style={{ color: COLORS.text.primary }}>{email.sender_name ?? email.sender}</strong>
                          {email.sender_name && <span style={{ marginLeft: SPACING[4], fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>&lt;{email.sender}&gt;</span>}
                        </p>
                        <p style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>
                          To: {email.recipients}
                        </p>
                        {email.cc_recipients && (
                          <p style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.tertiary }}>
                            CC: {email.cc_recipients}
                          </p>
                        )}
                        <p style={{ marginTop: SPACING[4], fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
                          {format(parseISO(email.received_at), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                        {(email.spam_flag || (email.spam_score !== null && email.spam_score !== undefined)) && (
                          <p style={{ marginTop: SPACING[4], fontSize: TYPOGRAPHY.body.sizes.tiny, color: email.spam_flag ? COLORS.status.error : COLORS.text.tertiary }}>
                            Spam score: {email.spam_score ?? '—'}
                            {email.spf_result && ` · SPF: ${email.spf_result}`}
                            {email.dkim_result && ` · DKIM: ${email.dkim_result}`}
                            {email.spam_flag && ' · flagged'}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        style={{ marginLeft: SPACING[16], color: closeButtonHovered ? COLORS.text.secondary : COLORS.text.tertiary, cursor: 'pointer', background: 'none', border: 'none' }}
                        onClick={onClose}
                        onMouseEnter={() => setCloseButtonHovered(true)}
                        onMouseLeave={() => setCloseButtonHovered(false)}
                      >
                        <XMarkIcon style={{ width: '24px', height: '24px' }} />
                      </button>
                    </div>

                    {/* Body Content */}
                    <div style={{
                      marginTop: SPACING[16],
                      flex: 1,
                      overflowY: 'auto',
                      minHeight: 0,
                      WebkitOverflowScrolling: 'touch',
                    }}>
                      <style>{`
                        .email-body {
                          font-family: ${TYPOGRAPHY.body.fontFamily};
                          font-size: 13px;
                          line-height: 1.6;
                          color: ${COLORS.text.primary};
                          word-wrap: break-word;
                          overflow-wrap: break-word;
                        }
                        .email-body p { margin: 0 0 8px 0; }
                        .email-body a { color: ${COLORS.accent.default}; text-decoration: none; }
                        .email-body a:hover { text-decoration: underline; }
                        .email-body img { max-width: 100%; height: auto; }
                        .email-body table { max-width: 100%; border-collapse: collapse; }
                        .email-body pre { white-space: pre-wrap; word-wrap: break-word; background: ${COLORS.bg.base}; padding: 8px; border-radius: 4px; font-size: 12px; }
                        .email-body blockquote {
                          margin: 8px 0 8px 0;
                          padding: 4px 0 4px 12px;
                          border-left: 3px solid ${COLORS.border.default};
                          color: ${COLORS.text.secondary};
                        }
                        .email-body blockquote blockquote {
                          border-left-color: ${COLORS.border.hover};
                        }
                        .email-body .gmail_quote {
                          margin: 12px 0 0 0;
                          padding: 8px 0 0 12px;
                          border-left: 3px solid ${COLORS.border.default};
                          color: ${COLORS.text.secondary};
                        }
                        .email-body .gmail_attr {
                          font-size: 12px;
                          color: ${COLORS.text.tertiary};
                          margin-bottom: 4px;
                        }
                        .email-body .gmail_quote .gmail_quote {
                          border-left-color: ${COLORS.border.hover};
                        }
                        .email-body .gmail_signature {
                          color: ${COLORS.text.secondary};
                          font-size: 12px;
                        }
                        .email-body div[style*="border-left"] { color: ${COLORS.text.secondary}; }
                        .email-body ul, .email-body ol { padding-left: 20px; margin: 8px 0; }
                        .email-body li { margin: 2px 0; }
                        .email-body hr { border: none; border-top: 1px solid ${COLORS.border.default}; margin: 12px 0; }
                        .email-body > div, .email-body > table { max-width: 100% !important; }
                      `}</style>
                      {bodyHtml && (
                        <div
                          className="email-body"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatEmailBody(bodyHtml), { ADD_DATA_URI_TAGS: ['img'] }) }}
                        />
                      )}
                    </div>

                    {/* Footer */}
                    <div style={{ marginTop: SPACING[24], display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${COLORS.border.default}`, paddingTop: SPACING[16] }}>
                      <div style={{ display: 'flex', gap: SPACING[8], flexWrap: 'wrap' }}>
                        {onMarkHandled && !email.is_handled && (
                          <Button
                            variant="ghost"
                            icon={<CheckCircleIcon style={{ width: '14px', height: '14px' }} />}
                            onClick={() => onMarkHandled(email.id)}
                            style={{ color: COLORS.status.success }}
                          >
                            Mark Handled
                          </Button>
                        )}
                        {onBlockSender && (
                          <Button
                            variant="ghost"
                            icon={<NoSymbolIcon style={{ width: '14px', height: '14px' }} />}
                            onClick={() => onBlockSender(email.id, 'email')}
                            style={{ color: COLORS.status.error }}
                          >
                            Block Sender
                          </Button>
                        )}
                        {onBlockSender && email.sender?.includes('@') && (
                          <Button
                            variant="ghost"
                            onClick={() => onBlockSender(email.id, 'domain')}
                            style={{ color: COLORS.status.error }}
                          >
                            Block @{email.sender.split('@')[1]}
                          </Button>
                        )}
                        {onMarkNewsletter && !email.is_newsletter && (
                          <Button
                            variant="ghost"
                            icon={<NewspaperIcon style={{ width: '14px', height: '14px' }} />}
                            onClick={() => onMarkNewsletter(email.id)}
                            style={{ color: COLORS.text.secondary }}
                          >
                            Mark Newsletter
                          </Button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: SPACING[8] }}>
                        {onReplyAll && (email.cc_recipients || email.recipients) && (
                          <Button
                            variant="ghost"
                            icon={<ArrowUturnLeftIcon style={{ width: '14px', height: '14px' }} />}
                            onClick={() => onReplyAll(email)}
                          >
                            Reply All
                          </Button>
                        )}
                        {onReply && (
                          <Button
                            variant="primary"
                            icon={<ArrowUturnLeftIcon style={{ width: '14px', height: '14px' }} />}
                            onClick={() => onReply(email)}
                          >
                            Reply
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EmailDetailModal;
